import { sectionTypes } from '../constants'

/**
 * Types for the Action Card structure.
 * These types are used throughout the application to ensure type safety and consistency.
 *
 * NOTE: Whenever changes are made to the Action Card types (in src/types/actionCard.ts), the zod schema should be updated accordingly. See README.md for details.
 */
export interface ActionCard {
  sections: Section[]
  templateId: string
  name: string
  isUiSimple: boolean
  commentedHeaders?: number[]
  // Is's needed to control of adding new props in the action card json
  // in order to add the props in the old already used action cards
  _updateVersion?: number
  conditionalQuestions?: number[]
  conditionalItems?: number[]
  isLockHidden?: boolean
  hasPinnedSections?: boolean
  isLocked?: boolean
  isFlowHidden?: boolean
  _calcScore?: true
  _isEnriched?: true
  scoreMode?: ScoreMode
  actionCardFlow?: Flow | null | undefined
  useScoringDimensions?: boolean
  scoringDimensions?: Dimension[]
  /** Timestamp when this action card was last synced with the playbook */
  publishedAt?: string
  version?: number
  score?: number
  enabledSeparators?: number[]
  touchedSeparators?: number[]
  lastActiveSeparator?: number
}

export interface Dimension {
  id: number
  name: string
  coefficient: number
  score?: number
  actual?: number
}

export enum ScoreBasis {
  SEPARATOR = 0,
  TOTAL = 1,
  DIMENSION = 2,
}

export interface Section {
  isSetTagsEnabled?: boolean
  showWidgets?: boolean
  recType: number | null
  isExcludedFromAi: boolean
  _originalId: number
  rank: number
  color: Color
  colorCode: 'None' | string
  hint: null | string
  id: number
  name: string
  parent: number
  type: sectionTypes
  score?: number
  widgets: WidgetData[] | null
  conditionRule?: ConditionRule
  elements?: Element[]
  // fragments?: Fragment[]
  hidden?: boolean
  idOfSep?: number
  isAutoCollapse?: boolean
  isLocked?: boolean // Only for the Separators type
  isOpened?: boolean
  isPinned?: boolean
  isRecommendation?: boolean
  isSectionMinimized?: boolean
  collapsePreviousHeader?: boolean
  maxScore?: number
  minScore?: number
  placeholder?: string
  scoreBased?: ScoreBasis
  scoreDeterminant?: number
  scoringDimensionId?: number
  setTags?: Tag[]
  tags?: Tag[]
  text?: string | null
  value?: unknown
}

export interface Color {
  rank?: number
  color: string
  id?: number
  name?: string
}

export interface Tag {
  id: number
  name: string
}

export interface Fragment {
  id: number
  tags: Tag[]
  name: string
  setTags: Tag[]
  kind: ElementKind
  _originalId: number
  isRecommendation: boolean
  widgets: WidgetData[] | null
  text?: string
  maxScore?: number
  minScore?: number
  scoreBased?: number
  hideFromWebPlay?: boolean
  scoringDimensionId?: number
  conditionRule?: ConditionRule | null
  includeInWebPlayNotification?: boolean
}

export enum AssessmentType {
  MULTI = 'multi select',
  SINGLE = 'single select',
}
export interface Assessment {
  altScoring?: boolean
  altScoringMax?: boolean
  autoSelectFirstChoice?: boolean
  checked?: boolean
  conditionRule?: ConditionRule
  dimensionRelatedScore?: number
  excludedFromScore?: boolean
  freeTextValue?: string
  hasFreeText?: boolean
  hideUnselected?: boolean
  _originalId: number
  id: number
  isAutoCollapse?: boolean
  isTouched?: boolean
  kind: ElementKind
  question: string
  required?: boolean
  responses: Response[]
  scoringDimensionId?: number
  superScore?: boolean
  tags?: Tag[]
  type: AssessmentType
}

export interface Response {
  id: number
  flow: Flow
  rank: number
  answer: string
  idOfSep: number
  checked: boolean
  _originalId: number
  superScore: boolean
  tags?: Tag[]
  iconClass?: string
  // __isInDom can be only true on not to be
  __isInDom?: true
}

export type Option = {
  id: number
  name: string
}

export type OptionWithRank = Option & { rank: number }

export enum QuestionTypes {
  DATE = 'Date',
  DATE_TIME = 'DateTime',
  PERCENTAGE = 'Percentage',
  TEXT = 'Text',
  CHECKBOX = 'Checkbox',
  NUMBER = 'Number',
  AMOUNT = 'Amount',
  DROPDOWN = 'Dropdown',
  LONG_TEXT = 'LongText',
  EMAIL = 'Email',
  FILE = 'File',
  PHONE = 'Phone',
  SIGNATURE = 'Signature',
  FORMULA = 'Formula',
}
export interface Question<T = unknown> {
  id: number
  type: QuestionTypes
  text: string
  rank: number
  kind: ElementKind
  _originalId: number
  idOfSep?: number
  value?: T
  data?: OptionWithRank[]
  tags?: Tag[]
  code?: string | null
  conditionRule?: ConditionRule
  thousand?: boolean
  scoringDimensionId?: number
  dimensionRelatedScore?: number
  required?: boolean
  formula?: string
  error?: string
  showCurrency?: boolean
}

export enum ScoreMode {
  ENTIRE = 'Entire',
  COVERED = 'Covered',
}

export enum ElementKind {
  ASSESSMENT = 'Assessment',
  RECOMMENDATION = 'Recommendation',
  QUESTION = 'Question',
  WIDGET = 'Widget',
}

export type Element = Partial<Question | Assessment | Fragment | WidgetData> & {
  required?: boolean
  kind: ElementKind
}

export type ElementWithCodeAndValue = (Question | WidgetData) & {
  kind: ElementKind
  value?: unknown
  required?: boolean
}

export type ElementWidthId = Element & { id: number }

export type DropdownItem = {
  id: number
  name: string
  rank: number
}

/**
 * Enum for the types of widgets that can be incorporated into various places of the Action Card.
 */
export enum WidgetType {
  GenericAiPrompt = 'GenericAiPrompt',
  TextToSpeech = 'TextToSpeech',
  UpdateWithAi = 'UpdateWithAi',
  /** @deprecated use 'Connect to CRM' instead.  */
  CreateCrmObject = 'CreateCrmObject',
  CodeSnippet = 'CodeSnippet',
  SetTag = 'SetTag',
  ConnectToCrm = 'ConnectToCrm',
  /** @deprecated Not supported.  */
  RecommendPlaybooks = 'RecommendPlaybooks',
  AiTeammate = 'AiTeammate',
}

export const SupportedWidgetTypes = Object.values(WidgetType).filter(
  x => x !== WidgetType.RecommendPlaybooks && x !== WidgetType.CreateCrmObject,
)

/**
 * Enum for the output modes of the AI widgets (where to display the generated text).
 */
export enum WidgetOutputMode {
  /** Append the generated text under the widget */
  Add = 'Add',
  /** Replace the recommendation text with the generated text (#10482) */
  Replace = 'Replace',
}

/**
 * Enum for the models of the AI widgets (where to display the generated text).
 */
export enum AiModel {
  Gpt54mini = 'gpt-5.4-mini',
  Gpt4oMini = 'gpt-4o-mini',
  Gpt4o = 'gpt-4o',
}

/**
 * Realtime model presets supported by the AI Teammate widget.
 */
export enum AiTeammateRealtimeModel {
  GptRealtime15 = 'gpt-realtime-1.5',
  GptRealtime2 = 'gpt-realtime-2',
  GptRealtime2Thinking = 'gpt-realtime-2-thinking',
  GptRealtimeMini = 'gpt-realtime-mini',
}

/**
 * Expected speech language for AI Teammate transcription.
 * `Auto` lets the transcription model detect the language on its own.
 */
export enum AiTeammateTranscriptionLanguage {
  Auto = 'auto',
  German = 'german',
  English = 'english',
}

/**
 * Scope of sections that the AI Teammate widget should analyze and fill out.
 */
export enum AiTeammateScope {
  EntirePlaybook = 'playbook',
  CurrentChapter = 'chapter',
  CurrentSection = 'section',
}

/* Object that supports states */
export interface StateSupportedObject {
  states: ObjectState[] | null
}

/**
 * Widget entity: represents an interactive widget
 * that can be incorporated into various places of the Action Card.
 * @story User Story 8109: Productize AI-supported sections
 */
export type WidgetData = StateSupportedObject & {
  /** Unique ID of the widget */
  id: number
  /** Type of the widget */
  type: WidgetType
  /** Logical rule for applying tag-based conditions */
  conditionRule: ConditionRule | null
  /** Tags that can be used to conditionally show/hide the widget */
  tags: Tag[] | null
  /** Tags that can be set by the object in order to influence visibility of other entities */
  setTags: Tag[] | null
  /** Unique code of the widget */
  code?: string | null
  /** Name of the widget */
  name: string
  /** Whether the widget should be triggered automatically upon reveal in Funnel view */
  autoLaunch: boolean
  /** Label of the button associated with the widget */
  buttonLabel: string
}

/**
 * Object state
 */
export interface ObjectState {
  type: ObjectStateType
  isActive: boolean | null
  activeTags: Tag[] | null
  inactiveTags: Tag[] | null
}

export type WidgetObjectState = ObjectState & {}

/**
 * Enum for the types of object state.
 */
export enum ObjectStateType {
  IsTagSet = 'IsTagSet',
}

export interface Data {
  id: number
  name: string
  rank: number
}

export enum ConditionRule {
  OR = 'OR',
  AND = 'AND',
  None = 'None',
}

export enum Flow {
  STOP = 'stop',
  PAUSE = 'pause',
  PLAY = 'play',
  COMPLETE = 'complete',
}
